/**
 * Kho icon xe dùng chung cho bản đồ giám sát.
 *
 * Vì sao cần: `VehicleMarker` dựng icon trong thân hàm render, mà `mergeVehicleTracking` tạo object
 * `vehicle` MỚI sau mỗi lô gói WebSocket (throttle 500 ms). Icon mới mỗi render nghĩa là
 * react-leaflet gọi `setIcon()` mỗi render, và Leaflet thay hẳn thẻ `<img>` của marker — xe nháy.
 *
 * Hai xe cùng loại, cùng trạng thái, cùng hướng (làm tròn) thì icon y hệt nhau, nên chỉ cần dựng
 * một lần rồi dùng lại. Trả về CÙNG MỘT object thì react-leaflet không thấy prop đổi và không đụng
 * tới DOM nữa.
 *
 * Cố tình không import React hay Leaflet ở đây: hàm dựng icon được truyền vào, nên file này test
 * được bằng jest mà không cần canvas (jsdom không có `toDataURL`).
 */

/**
 * Bước làm tròn hướng xe, tính bằng độ.
 *
 * Hướng từ thiết bị đổi gần như mỗi gói, kể cả khi xe đi thẳng. Không làm tròn thì khoá cache đổi
 * liên tục và kho icon vô dụng. 5° là mức mắt thường không phân biệt được trên icon 32 px.
 */
export const DIRECTION_STEP = 5;

/** Icon chưa vẽ xong thì URL không phải `data:image` — xem `isRenderedIcon`. */
const DATA_URL_PREFIX = 'data:image';

export const roundDirection = direction => {
  const deg = Number(direction);
  if (!Number.isFinite(deg)) return 0;
  const wrapped = ((deg % 360) + 360) % 360;
  return (Math.round(wrapped / DIRECTION_STEP) * DIRECTION_STEP) % 360;
};

/**
 * Khoá định danh một icon. Đủ bốn thứ quyết định hình vẽ: nợ cước, trạng thái, loại xe, hướng.
 */
export const iconCacheKey = vehicle => {
  if (!vehicle) return 'none';
  if (vehicle.debt) return 'debt';

  return [
    vehicle.device_status || 'unknown',
    vehicle.vehicle_type || 'unknown',
    roundDirection(vehicle.direction)
  ].join('|');
};

/**
 * Icon đã vẽ xong hay chưa.
 *
 * `RotateIcon` vẽ lên canvas rồi xuất `toDataURL()`. Khi ảnh nền chưa tải xong, nó trả về ảnh nền
 * gốc (`.svg`) làm phương án dự phòng — hình vẫn hiện, chỉ là chưa xoay. Bản dự phòng đó KHÔNG
 * được cache, nếu không thì một lần lỡ nhịp sẽ ghim chiếc xe ở hướng sai mãi mãi.
 */
export const isRenderedIcon = icon => {
  const url = icon && icon.options && icon.options.iconUrl;
  return typeof url === 'string' && url.startsWith(DATA_URL_PREFIX);
};

const cache = new Map();

/**
 * Lấy icon cho một xe, dựng qua `build` nếu chưa có.
 *
 * @param {object} vehicle - bản ghi xe từ luồng tracking
 * @param {() => object} build - hàm dựng icon Leaflet, chỉ được gọi khi cache trượt
 * @returns {object} icon Leaflet — cùng khoá thì cùng một object
 */
export const getVehicleIcon = (vehicle, build) => {
  const key = iconCacheKey(vehicle);
  const cached = cache.get(key);
  if (cached) return cached;

  const icon = build();
  if (isRenderedIcon(icon)) cache.set(key, icon);

  return icon;
};

/** Dọn kho — chỉ dùng trong test. */
export const resetVehicleIconCache = () => cache.clear();

/** Số icon đang giữ — chỉ dùng trong test và khi soi hiệu năng. */
export const vehicleIconCacheSize = () => cache.size;
